import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FadeUp, Overline } from "../components/Reveal";
import { LogOut, Package, MapPin, Ticket, ShieldCheck } from "lucide-react";
import { OrdersTab } from "../components/account/OrdersTab";
import { AddressTab } from "../components/account/AddressTab";
import { CouponsTab } from "../components/account/CouponsTab";
import { PrivacyTab } from "../components/account/PrivacyTab";

const tabs = [
  { id: "orders", label: "Order History", icon: Package, el: OrdersTab },
  { id: "address", label: "Your Address", icon: MapPin, el: AddressTab },
  { id: "coupons", label: "Your Coupons", icon: Ticket, el: CouponsTab },
  { id: "privacy", label: "Data Privacy", icon: ShieldCheck, el: PrivacyTab },
];

const Account = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (user === false) navigate("/auth?next=/account");
  }, [user, navigate]);

  if (!user) return <div className="min-h-screen" />;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const Active = tabs.find((t) => t.id === tab).el;

  return (
    <div data-testid="account-page" className="min-h-screen pt-32 md:pt-40 pb-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <Overline>Members Quarter</Overline>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5]">
              {user.name || "Conqueror"}
            </h1>
            <p className="mt-2 text-sm text-zinc-500">{user.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 px-8 py-3 border border-white/20 text-zinc-400 text-xs tracking-[0.3em] uppercase hover:border-maroon-light hover:text-[#F5F5F5] hover:bg-maroon/40 transition-colors duration-500"
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>

        <FadeUp className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <nav className="md:col-span-3 flex md:flex-col gap-1 overflow-x-auto border-b md:border-b-0 md:border-r border-white/10 pb-3 md:pb-0 md:pr-6" data-testid="account-tabs">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`account-tab-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-3 px-4 py-3 text-left text-xs tracking-[0.25em] uppercase whitespace-nowrap border-l-2 transition-colors duration-300 ${
                  tab === id
                    ? "border-maroon-light text-[#F5F5F5] bg-maroon-deep/30"
                    : "border-transparent text-zinc-500 hover:text-zinc-200"
                }`}
              >
                <Icon size={14} className={tab === id ? "text-gold" : ""} /> {label}
              </button>
            ))}
          </nav>
          <div className="md:col-span-9" data-testid={`account-panel-${tab}`}>
            <Active key={tab} />
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

export default Account;
